<template>
  <div class="JOB-LIST">
    <transition :name="transitionDirection">
      <div :key="jobsStatusAlias" class="JOB-LIST-CONTAINER">
        <transition name="VERTICAL" mode="out-in">
          <transition-group
              v-if="meta.count || isFetching || isCanceled"
              v-infinite-scroll="{
                offset: 666,
                promise: meta.hasMore && !isFetching && loadMoreJobs
              }"
              name="SWIPE"
              @before-leave="onListItemBeforeLeave"
              tag="div"
              class="d-flex flex-1-1 fd-column ov-y-scroll">
            <job-list-item
                v-for="(job, jobNumber) in rawJobs"
                :key="job"
                :id="job"
                class="LIST-ITEM"
                :selenium="`current-job-job-item:${jobNumber}`"
                @change="change"/>
            <template v-if="isFetching || isCanceled">
              <job-list-item-placeholder
                  v-for="(placeholder, index) in 3"
                  :key="`placeholder-${index}`"
                  class="LIST-ITEM"
              />
            </template>
          </transition-group>
          <job-list-placeholder v-else-if="!isUninitialized" />
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import jobParams from '@/mixins/job-params';
import jobs from '@/mixins/jobs';

import JobListPlaceholder from './JobListPlaceholder';
import JobListItem from './JobListItem';
import JobListItemPlaceholder from './JobListItemPlaceholder';

export default {
  name: 'job-list',
  components: {
    JobListPlaceholder,
    JobListItem,
    JobListItemPlaceholder,
  },
  mixins: [
    jobParams,
    jobs,
  ],
  data () {
    return {
      transitionDirection: 'NONE',
    };
  },
  computed: {
    ...mapGetters('account/pro/info', [
      'serviceProviderId',
    ]),
    isUninitialized () {
      return this.meta.status === this.$options.REQUEST_LIFECYCLE.UNINITIALIZED;
    },
    isFetching () {
      return this.meta.status === this.$options.REQUEST_LIFECYCLE.FETCHING;
    },
    isFulfilled () {
      return this.meta.status === this.$options.REQUEST_LIFECYCLE.FULFILLED;
    },
    isCanceled () {
      return this.meta.status === this.$options.REQUEST_LIFECYCLE.CANCELED;
    },
  },
  watch: {
    jobsStatusAlias (jobsStatusAlias, oldJobsStatusAlias) {
      this.clearJobs();
      this.loadJobs();

      const jobsStatusAliases = Object.keys(this.$options.JOBS_STATUS_ALIASES);
      const oldJobsStatusAliasIndex = jobsStatusAliases.indexOf(
        oldJobsStatusAlias
      );
      const jobsStatusAliasIndex = jobsStatusAliases.indexOf(jobsStatusAlias);

      this.transitionDirection = jobsStatusAliasIndex < oldJobsStatusAliasIndex ? 'LEFT' : 'RIGHT';
    },
    customJobsQuery (cur, prev) {
      if (this.isSearchStatusAlias && prev !== cur) {
        this.clearJobs();
        this.loadJobs();
      }
    },
    customJobsStatuses (cur, prev) {
      if (this.isPastStatusAlias && prev.join(',') !== cur.join(',')) {
        this.clearJobs();
        this.loadJobs();
      }
    },
    customJobsSubStatuses (cur, prev) {
      if (this.isPotentialStatusAlias && prev !== cur) {
        this.clearJobs();
        this.loadJobs();
      }
    },
    rawJobs () {
      this.setActiveJobId();
    },
  },
  mounted () {
    if (this.isPastOrSearchStatusAlias) {
      this.clearJobs();
    } else {
      this.setActiveJobId();
    }

    if (!this.isFulfilled || !this.meta.count) {
      this.loadJobs();
    }
  },
  methods: {
    async loadJobs (params) {
      let ids;
      let q;

      if (this.isSearchStatusAlias) {
        ids = this.customJobsIds;

        if (!ids.length) {
          ids = undefined;
          q = this.customJobsQuery;
        }
      }

      const matchStatuses = this.isPastStatusAlias && this.customJobsStatuses.length
        ? this.customJobsStatuses
        : this.$options.JOBS_STATUS_ALIASES[this.jobsStatusAlias].statuses;
      await this.fetchWorkOrderMatches({
        includeAppointments: true,
        jobsStatusAlias: this.jobsStatusAlias,
        params: {
          size: 100,
          statuses: matchStatuses.join(','),
          q,
          sort: this.isPastStatusAlias ? 'match_time_updated desc' : 'time_created desc',
          service_provider_id: this.serviceProviderId,
          ...params,
        },
      });

      if (this.isSearchStatusAlias) {
        this.$events.$emit(this.$events.PRO_JOB_SEARCH, {
          jobCount: this.meta.total,
          query: q,
        });
      }
    },
    async loadMoreJobs () {
      await this.loadJobs({ from: this.meta.count });
    },
    clearJobs () {
      this.resetWorkOrderMatches({ jobsStatusAlias: this.jobsStatusAlias });
    },
    // This method selects first job in the list if jobId is not provided through URL params
    setActiveJobId () {
      if (!this.$device.isMobile && !this.jobId && this.rawJobs[0]) {
        // After introducing navigation quards in edit forms we can't trigger this synchronously anymore.
        this.$nextTick(() => {
          this.$router.replace({
            name: 'dashboard::pro::jobs',
            params: {
              jobId: this.rawJobs[0],
            },
            query: this.$route.query,
          });
        });
      }
    },
    onListItemBeforeLeave (el) {
      const { top: parentTop } = el.parentElement.getBoundingClientRect();
      const { top } = el.getBoundingClientRect();

      el.style.top = `${top - parentTop}px`;

      global.requestAnimationFrame(() => {
        el.style.position = 'absolute';
      });
    },
    change () {
      this.$emit('change');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";

  .JOB-LIST {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  .JOB-LIST-CONTAINER {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
  }

  .LEFT-enter-active,
  .RIGHT-enter-active,
  .LEFT-leave-active,
  .RIGHT-leave-active {
    @include absolute(0, 0, 0, 0);
    transform: none;
    will-change: transform;
    transition: transform $transition-duration;

    &.LEFT-enter,
    &.RIGHT-leave-to {
      transform: translateX(-100%);
    }

    &.LEFT-leave-to,
    &.RIGHT-enter {
      transform: translateX(100%);
    }
  }

  .VERTICAL-enter-active,
  .VERTICAL-leave-active {
    opacity: 1;
    transform: none;
    will-change: transform, opacity;
    transition: transform $transition-half-duration,
      opacity $transition-half-duration;

    &.VERTICAL-enter,
    &.VERTICAL-leave-to {
      opacity: 0;
      transform: translateY(30px);
    }
  }

  .LIST-ITEM {
    opacity: 1;
    transform: none;
    transition: all $transition-duration;

    &.SWIPE-enter,
    &.SWIPE-leave-to {
      opacity: 0;
    }

    &.SWIPE-enter {
      transform: translateY(30px);
    }

    &.SWIPE-leave-to {
      transform: translateX(100%);
    }
  }
</style>
